import { TranslationFunction } from '../common/types'
import { FormattedPropertyData } from '../models/property-details-page/property-details-model'
import { PostalAddress, Accommodation, AggregateRating, Review, GeoCoordinates } from 'schema-dts'
import isEmpty from 'lodash/isEmpty'
import { SuitableStates } from '../models/property-details-page/suitables-model'
import {
    ExposeTransport,
    PriceDetailsSchemaTransport,
    PropertyObjectTransport,
    PropertyRooms,
    VacationRegionNodeTransport,
} from '../transports/property-details-page-transport'

const createBedDetails = (objectRooms: any, roomsCount: number) => {
    const bedDetails =
        objectRooms?.rooms?.flatMap(
            (room: any) =>
                room.beds
                    ?.filter((bed: any) => bed.count > 0)
                    .map((bed: any) => ({
                        '@type': 'BedDetails',
                        typeOfBed: bed.label,
                        numberOfBeds: bed.count,
                    })) ?? [],
        ) ?? []

    if (bedDetails.length === 0 && roomsCount > 0) {
        return [
            {
                '@type': 'BedDetails',
                typeOfBed: 'Bed',
                numberOfBeds: roomsCount,
            },
        ]
    }

    return bedDetails.length > 0 ? bedDetails : undefined
}

const createAddress = (region: VacationRegionNodeTransport | undefined, address: any): PostalAddress => ({
    '@type': 'PostalAddress',
    ...(region?.title && { addressLocality: region.title }),
    ...(address?.zipCode && { postalCode: address.zipCode }),
    ...(address?.city && { addressRegion: address.city }),
    ...(address?.street && { streetAddress: address.street }),
    ...(address?.country && { addressCountry: address.country }),
})

const createReviews = (objectRatings: any): Review[] => {
    return (
        objectRatings?.ratings
            ?.filter(
                (rating: any) =>
                    rating.ratingDate !== null && rating.ratingDate !== undefined && rating.ratingDate !== '',
            )
            .slice(0, 12)
            .map((rating: any) => {
                const authorName =
                    `${rating.contact?.firstName || ''} ${rating.contact?.lastName || ''}`.trim() ||
                    `${rating.contact?.lastName || ''} from ${rating.contact?.address?.city || ''}`.trim() ||
                    'Anonymous'

                const review: Review = {
                    '@type': 'Review',
                    reviewRating: {
                        '@type': 'Rating',
                        ratingValue: Math.max(1, Math.min(5, Number(rating.rating?.average) || 3)),
                        bestRating: 5,
                    },
                    author: {
                        '@type': 'Person',
                        name: authorName,
                    },
                }

                review.datePublished = new Date(rating.ratingDate).toISOString().split('T')[0]

                if (rating.travelDateFrom) {
                    review.contentReferenceTime = new Date(rating.travelDateFrom).toISOString().split('T')[0]
                }

                return review
            }) ?? []
    )
}

export const getVacationRentalSchemaForProperty = ({
    propertyDetails,
    vacationRegionNode,
    objectDetails,
    rooms,
    prices,
    expose,
    t,
}: {
    propertyDetails: FormattedPropertyData | null
    vacationRegionNode?: VacationRegionNodeTransport
    objectDetails?: PropertyObjectTransport | null
    rooms?: PropertyRooms
    prices?: PriceDetailsSchemaTransport
    expose?: ExposeTransport
    t: TranslationFunction
}): { __html: string } | null => {
    if (!propertyDetails || isEmpty(propertyDetails) || !propertyDetails.reviews?.ratingsCount) {
        return null
    }

    const { fullTitle: name, description, gallery, reviews, stats, features, id, general } = propertyDetails
    const { badge, ratingsCount } = reviews
    const { longitude, latitude } = objectDetails?.location ?? {}
    const { address: objectAddress } = expose?.objectLocation ?? {}
    const objectRatings = expose?.objectRatings
    const objectRooms = expose?.objectRooms

    const aggregateRating: AggregateRating = {
        '@type': 'AggregateRating',
        bestRating: '5',
        worstRating: '1',
        ratingValue: String(Math.max(1, Math.min(5, Number(badge?.avg) || 3))),
        reviewCount: ratingsCount,
    }

    const individualReviews = createReviews(objectRatings)
    const address = createAddress(vacationRegionNode, objectAddress)
    const geo: GeoCoordinates | undefined =
        longitude && latitude ? { '@type': 'GeoCoordinates', latitude, longitude } : undefined

    const roomsCount = rooms?.rooms?.reduce((count, room) => (room.type ? count + room.count : count), 0) || 0

    const containsPlace = {
        '@type': 'Accommodation',
        additionalType: general?.isComboListing ? 'HolidayHome' : 'EntirePlace',
        occupancy: stats?.maxPersons ? { '@type': 'QuantitativeValue', value: stats.maxPersons } : undefined,
        numberOfBedrooms: stats?.roomCount,
        numberOfBathroomsTotal: stats?.bathrooms,
        numberOfRooms: roomsCount,
        bed: createBedDetails(objectRooms, roomsCount),
        amenityFeature: features
            ? Object.entries(features)
                  .slice(0, 5)
                  .map(([key, value]) => ({
                      '@type': 'LocationFeatureSpecification',
                      name: key.replace(/-/g, ' '),
                      value: Number(value.state) === SuitableStates.SUITABLE_STATUS_ALLOWED,
                  }))
            : undefined,
        floorSize: stats?.apartmentSize
            ? {
                  '@type': 'QuantitativeValue',
                  value: stats.apartmentSize,
                  unitCode: 'SQM',
              }
            : undefined,
        geo,
    } as Accommodation

    return {
        __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'VacationRental',
            name,
            address,
            description: description?.text,
            image: gallery?.slice(0, 8).map(image => image.src),
            url: `${t('traumBaseUrl')}/${id}`,
            identifier: id,
            aggregateRating,
            geo,
            containsPlace,
            priceRange: prices?.price ?? null,
            review: individualReviews,
            additionalType: 'House',
        }),
    }
}

export const getProductSchemaForProperty = ({
    propertyDetails,
    prices,
}: {
    propertyDetails: FormattedPropertyData | null
    prices?: PriceDetailsSchemaTransport
}): { __html: string } | null => {
    if (!propertyDetails || isEmpty(propertyDetails) || !propertyDetails.reviews?.ratingsCount) {
        return null
    }

    const { fullTitle: name, description, gallery, reviews, id } = propertyDetails
    const { badge, ratingsCount } = reviews

    const aggregateRating: AggregateRating = {
        '@type': 'AggregateRating',
        bestRating: '5',
        worstRating: '1',
        ratingValue: String(Math.max(1, Math.min(5, Number(badge?.avg) || 3))),
        reviewCount: ratingsCount,
    }

    return {
        __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name,
            description: description?.text,
            image: gallery?.slice(0, 8).map(image => image.src),
            identifier: id,
            aggregateRating,
            priceRange: prices?.price ?? null,
        }),
    }
}
